/**
 * Table Answer Utilities
 * Utility functions and default configuration for table-based homework questions
 */

// Default configuration for a new table answer
export const defaultTableConfig = {
  columns: [
    { id: 'col_0', header: 'x', type: 'math' },
    { id: 'col_1', header: 'y', type: 'math' }
  ],
  rows: [
    {
      id: 'row_0',
      cells: {
        'col_0': { value: '', prefilled: false },
        'col_1': { value: '', prefilled: false }
      }
    }
  ],
  grading: {
    mode: 'exact',           // 'exact' | 'tolerance'
    tolerance: 0.01,
    rowOrderFlexible: false,
    caseSensitive: false
  }
};

// Generate a unique ID for columns/rows
let idCounter = 0;
export const generateId = (prefix = 'id') => {
  idCounter += 1;
  return `${prefix}_${Date.now()}_${idCounter}`;
};

/**
 * Parse a LaTeX string to a numerical value
 * Handles common formats: fractions, decimals, integers, negatives
 * @param {string} latex - LaTeX string to parse
 * @returns {number} - Parsed number or NaN if not parseable
 */
export const parseLatexToNumber = (latex) => {
  if (latex === null || latex === undefined || latex === '') {
    return NaN;
  }

  let str = String(latex).trim();

  // Remove LaTeX formatting
  str = str.replace(/\\,/g, '');  // Remove thin spaces
  str = str.replace(/\s+/g, '');  // Remove whitespace

  // Handle negative numbers
  const isNegative = str.startsWith('-') || str.startsWith('\\-');
  if (isNegative) {
    str = str.replace(/^-|^\\-/, '');
  }

  // Handle fractions: \frac{a}{b} or \dfrac{a}{b}
  const fracMatch = str.match(/\\(?:d)?frac\{([^}]+)\}\{([^}]+)\}/);
  if (fracMatch) {
    const numerator = parseLatexToNumber(fracMatch[1]);
    const denominator = parseLatexToNumber(fracMatch[2]);
    if (!isNaN(numerator) && !isNaN(denominator) && denominator !== 0) {
      const result = numerator / denominator;
      return isNegative ? -result : result;
    }
    return NaN;
  }

  // Handle simple fractions: a/b
  const simpleFracMatch = str.match(/^([+-]?\d+(?:\.\d+)?)\s*\/\s*([+-]?\d+(?:\.\d+)?)$/);
  if (simpleFracMatch) {
    const numerator = parseFloat(simpleFracMatch[1]);
    const denominator = parseFloat(simpleFracMatch[2]);
    if (!isNaN(numerator) && !isNaN(denominator) && denominator !== 0) {
      const result = numerator / denominator;
      return isNegative ? -result : result;
    }
    return NaN;
  }

  // Handle square roots: \sqrt{a} or \sqrt[n]{a}
  const sqrtMatch = str.match(/\\sqrt(?:\[(\d+)\])?\{([^}]+)\}/);
  if (sqrtMatch) {
    const n = sqrtMatch[1] ? parseInt(sqrtMatch[1], 10) : 2;
    const value = parseLatexToNumber(sqrtMatch[2]);
    if (!isNaN(value)) {
      const result = Math.pow(value, 1 / n);
      return isNegative ? -result : result;
    }
    return NaN;
  }

  // Handle pi
  if (str === '\\pi' || str === 'pi') {
    return isNegative ? -Math.PI : Math.PI;
  }

  // Handle e (Euler's number)
  if (str === 'e') {
    return isNegative ? -Math.E : Math.E;
  }

  // Handle plain numbers (including decimals)
  const numMatch = str.match(/^([+-]?\d+(?:\.\d+)?)$/);
  if (numMatch) {
    const result = parseFloat(numMatch[1]);
    return isNegative ? -result : result;
  }

  return NaN;
};

/**
 * Normalize LaTeX string for string comparison
 * @param {string} latex - LaTeX string to normalize
 * @returns {string} - Normalized string
 */
export const normalizeLatex = (latex) => {
  if (!latex) return '';
  return String(latex)
    .replace(/\s+/g, '')
    .replace(/\\,/g, '')
    .toLowerCase();
};

/**
 * Compare two values based on type and tolerance
 * @param {string} studentValue - Student's answer
 * @param {string} correctValue - Correct answer
 * @param {string} type - 'math' or 'text'
 * @param {number} tolerance - Tolerance for numeric comparison
 * @param {boolean} caseSensitive - Case sensitivity for text comparison
 * @returns {boolean} - Whether values match
 */
export const valuesMatch = (studentValue, correctValue, type, tolerance = 0.01, caseSensitive = false) => {
  const student = String(studentValue || '').trim();
  const correct = String(correctValue || '').trim();

  if (student === '' && correct === '') return true;
  if (student === '' || correct === '') return false;

  if (type === 'math') {
    const studentNum = parseLatexToNumber(student);
    const correctNum = parseLatexToNumber(correct);

    // If both are valid numbers, compare with tolerance
    if (!isNaN(studentNum) && !isNaN(correctNum)) {
      return Math.abs(studentNum - correctNum) <= tolerance;
    }

    // Fallback to normalized string comparison
    return normalizeLatex(student) === normalizeLatex(correct);
  }

  // Text comparison - normalize whitespace
  const studentNormalized = student.replace(/\s+/g, '');
  const correctNormalized = correct.replace(/\s+/g, '');

  if (caseSensitive) {
    return studentNormalized === correctNormalized;
  }
  return studentNormalized.toLowerCase() === correctNormalized.toLowerCase();
};

/**
 * Create an empty row with cells for all columns
 * @param {Array} columns - Column definitions
 * @returns {Object} - New row object
 */
export const createEmptyRow = (columns) => {
  const cells = {};
  columns.forEach(col => {
    cells[col.id] = { value: '', prefilled: false };
  });
  return {
    id: generateId('row'),
    cells
  };
};

/**
 * Create a new column definition
 * @param {string} header - Column header text
 * @param {string} type - Column type ('math' or 'text')
 * @returns {Object} - New column object
 */
export const createColumn = (header = '', type = 'math') => {
  return {
    id: generateId('col'),
    header,
    type
  };
};

/**
 * Add a new column to the table config and update all rows
 * @param {Object} config - Current table config
 * @param {string} header - New column header
 * @param {string} type - Column type
 * @returns {Object} - Updated config
 */
export const addColumn = (config, header = '', type = 'math') => {
  // Defensive: ensure config has required structure
  if (!config || !Array.isArray(config.columns) || !Array.isArray(config.rows)) {
    console.warn('addColumn: Invalid config, using default');
    config = defaultTableConfig;
  }
  const newColumn = createColumn(header, type);
  const updatedRows = config.rows.map(row => ({
    ...row,
    cells: {
      ...row.cells,
      [newColumn.id]: { value: '', prefilled: false }
    }
  }));

  return {
    ...config,
    columns: [...config.columns, newColumn],
    rows: updatedRows
  };
};

/**
 * Remove a column from the table config
 * @param {Object} config - Current table config
 * @param {string} columnId - ID of column to remove
 * @returns {Object} - Updated config
 */
export const removeColumn = (config, columnId) => {
  const updatedColumns = config.columns.filter(col => col.id !== columnId);
  const updatedRows = config.rows.map(row => {
    const { [columnId]: removed, ...remainingCells } = row.cells;
    return { ...row, cells: remainingCells };
  });

  return {
    ...config,
    columns: updatedColumns,
    rows: updatedRows
  };
};

/**
 * Add a new row to the table config
 * @param {Object} config - Current table config
 * @returns {Object} - Updated config
 */
export const addRow = (config) => {
  // Defensive: ensure config has required structure
  if (!config || !Array.isArray(config.columns) || !Array.isArray(config.rows)) {
    console.warn('addRow: Invalid config, using default');
    config = defaultTableConfig;
  }
  const newRow = createEmptyRow(config.columns);
  return {
    ...config,
    rows: [...config.rows, newRow]
  };
};

/**
 * Remove a row from the table config
 * @param {Object} config - Current table config
 * @param {string} rowId - ID of row to remove
 * @returns {Object} - Updated config
 */
export const removeRow = (config, rowId) => {
  return {
    ...config,
    rows: config.rows.filter(row => row.id !== rowId)
  };
};

/**
 * Update a cell value in the config
 * @param {Object} config - Current table config
 * @param {string} rowId - Row ID
 * @param {string} colId - Column ID
 * @param {Object} cellUpdate - Properties to update { value?, prefilled? }
 * @returns {Object} - Updated config
 */
export const updateCell = (config, rowId, colId, cellUpdate) => {
  return {
    ...config,
    rows: config.rows.map(row => {
      if (row.id !== rowId) return row;
      return {
        ...row,
        cells: {
          ...row.cells,
          [colId]: {
            ...row.cells[colId],
            ...cellUpdate
          }
        }
      };
    })
  };
};

/**
 * Update a column header
 * @param {Object} config - Current table config
 * @param {string} columnId - Column ID
 * @param {string} header - New header value
 * @returns {Object} - Updated config
 */
export const updateColumnHeader = (config, columnId, header) => {
  return {
    ...config,
    columns: config.columns.map(col => {
      if (col.id !== columnId) return col;
      return { ...col, header };
    })
  };
};

/**
 * Update column type
 * @param {Object} config - Current table config
 * @param {string} columnId - Column ID
 * @param {string} type - New type ('math' or 'text')
 * @returns {Object} - Updated config
 */
export const updateColumnType = (config, columnId, type) => {
  return {
    ...config,
    columns: config.columns.map(col => {
      if (col.id !== columnId) return col;
      return { ...col, type };
    })
  };
};

/**
 * Extract correct answer from config (values of all non-prefilled cells)
 * @param {Object} config - Table config with prefilled values
 * @returns {Object} - Answer object with row values
 */
export const extractAnswerFromConfig = (config) => {
  return {
    rows: config.rows.map(row => ({
      id: row.id,
      cells: Object.entries(row.cells).reduce((acc, [colId, cell]) => {
        acc[colId] = cell.value;
        return acc;
      }, {})
    }))
  };
};

/**
 * Initialize student answer from config (prefilled values only)
 * @param {Object} config - Table config
 * @returns {Object} - Initial student answer
 */
export const initializeStudentAnswer = (config) => {
  return {
    rows: config.rows.map(row => ({
      id: row.id,
      cells: Object.entries(row.cells).reduce((acc, [colId, cell]) => {
        // Only include prefilled values as starting point
        acc[colId] = cell.prefilled ? cell.value : '';
        return acc;
      }, {})
    }))
  };
};

/**
 * Merge student answer with any updates
 * @param {Object} currentAnswer - Current student answer
 * @param {string} rowId - Row ID to update
 * @param {string} colId - Column ID to update
 * @param {string} value - New value
 * @returns {Object} - Updated answer
 */
export const updateStudentAnswer = (currentAnswer, rowId, colId, value) => {
  return {
    ...currentAnswer,
    rows: currentAnswer.rows.map(row => {
      if (row.id !== rowId) return row;
      return {
        ...row,
        cells: {
          ...row.cells,
          [colId]: value
        }
      };
    })
  };
};

/**
 * Generate full answer logic description for table questions (full mode)
 * @param {Object} config - Table configuration with columns, rows, grading
 * @returns {Object|null} - Description object with structure info
 */
export const generateTableAnswerLogicDescription = (config) => {
  if (!config) return null;

  const columns = config.columns || [];
  const rows = config.rows || [];

  // Count prefilled vs editable cells
  let prefilledCount = 0;
  let editableCount = 0;

  rows.forEach(row => {
    columns.forEach(col => {
      const cell = row.cells?.[col.id];
      if (cell?.prefilled) {
        prefilledCount++;
      } else {
        editableCount++;
      }
    });
  });

  // Get column info
  const columnHeaders = columns.map(col => col.header || col.id);
  const columnTypes = columns.map(col => ({
    header: col.header || col.id,
    type: col.type || 'math'
  }));

  return {
    columnCount: columns.length,
    rowCount: rows.length,
    prefilledCount,
    editableCount,
    columnHeaders,
    columnTypes
  };
};

/**
 * Generate general comparison rules for table questions (preview mode)
 * @param {Object} config - Table configuration with grading settings
 * @returns {Array<string>} - Array of rule strings
 */
export const generateTableComparisonRules = (config) => {
  if (!config) return [];

  const rules = [];
  const columns = config.columns || [];
  const grading = config.grading || {};
  const caseSensitive = grading.caseSensitive || false;

  // Check column types
  const hasMathColumns = columns.some(col => col.type === 'math' || !col.type);
  const hasTextColumns = columns.some(col => col.type === 'text');

  if (hasMathColumns) {
    rules.push('\u2022 Math cells: Fractions (1/2), square roots (\u221A2), and \u03C0 are parsed');
  }

  if (hasTextColumns) {
    rules.push(`\u2022 Text cells: Compared as text (case ${caseSensitive ? 'sensitive' : 'insensitive'})`);
  }

  rules.push('\u2022 Prefilled cells are not graded');

  return rules;
};
